import React from 'react'
import { useTranslation } from 'react-i18next'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import { motion } from 'framer-motion'
import PageSEO from '../../shared/PageSEO'
import CardMedia from '@mui/material/CardMedia'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Button from '@mui/material/Button'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Link, useLocation } from 'react-router-dom'
import { blogs } from '../BlogList'

// --- Reusable styled components ---
const StyledTitle = ({ children }) => (
  <Typography
    variant="h4"
    sx={{
      mb: 1,
      fontWeight: 'bold',
      background: 'linear-gradient(45deg, #007FFF 30%, #0059B2 90%)',
      backgroundClip: 'text',
      textFillColor: 'transparent',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    }}
  >
    {children}
  </Typography>
)

const StyledDate = ({ children }) => (
  <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 4 }}>
    {children}
  </Typography>
)

const SectionTitle = ({ title }) => {
  if (!title) return null
  return (
    <Typography
      variant="h5"
      sx={{
        mb: 2,
        fontWeight: 'bold',
        fontSize: '1.3rem',
        color: 'text.primary'
      }}
    >
      {title}
    </Typography>
  )
}

const BlogImage = ({ image, alt }) => {
  if (!image || image === 'mainImage') return null
  return (
    <Box sx={{ mb: 4 }}>
      <CardMedia
        component="img"
        image={image}
        alt={alt}
        sx={{
          width: '100%',
          maxHeight: '500px',
          objectFit: 'cover',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}
      />
    </Box>
  )
}

const ListContent = ({ items }) => (
  <List>
    {items.map((item, index) => (
      <ListItem key={index} sx={{ py: 0.5 }}>
        <ListItemText
          primary={item}
          sx={{
            '& .MuiListItemText-primary': {
              fontSize: '1.1rem',
              lineHeight: 1.8,
              color: 'text.primary',
              textAlign: 'justify'
            }
          }}
        />
      </ListItem>
    ))}
  </List>
)

const renderContentWithLinksAndEmphasis = (text) => {
  if (typeof text !== 'string') return null

  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
  const emRegex = /<em>(.*?)<\/em>/g
  const boldRegex = /\*\*(.*?)\*\*/g

  const combinedRegex = new RegExp(`${linkRegex.source}|${emRegex.source}|${boldRegex.source}`, 'g')

  const parts = []
  let lastIndex = 0
  let match

  while ((match = combinedRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index))
    }

    if (match[1] && match[2]) {
      // link: [label](href)
      const href = match[2]
      const isInternal = href.startsWith('/')
      parts.push(
        <a
          key={href + match.index}
          href={href}
          target={isInternal ? '_self' : '_blank'}
          rel={isInternal ? undefined : 'noreferrer'}
          style={{
            color: '#1976d2',
            textDecoration: 'none',
            borderBottom: '1px solid #1976d2'
          }}
        >
          {match[1]}
        </a>
      )
    } else if (match[3]) {
      // em: <em>text</em>
      parts.push(
        <em key={'em-' + match.index} style={{ fontStyle: 'italic' }}>
          {match[3]}
        </em>
      )
    } else if (match[4]) {
      // bold: **text**
      parts.push(
        <strong key={'b-' + match.index} style={{ fontWeight: 700 }}>
          {match[4]}
        </strong>
      )
    }

    lastIndex = combinedRegex.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex))
  }

  return parts
}

const TextContent = ({ content }) => {
  const safeContent = typeof content === 'string' ? content : ''
  const isKorean = /[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(safeContent)

  return (
    <Typography
      variant="body1"
      sx={{
        lineHeight: 1.6,
        color: 'text.primary',
        whiteSpace: 'pre-wrap',
        textAlign: isKorean ? 'justify' : 'left',
        fontSize: '1rem',
        letterSpacing: 'normal',
        wordSpacing: 'normal',
        '& p': {
          marginBottom: '1rem'
        }
      }}
    >
      {renderContentWithLinksAndEmphasis(safeContent)}
    </Typography>
  )
}

const Section = ({ content, title, isList }) => (
  <Box sx={{ mb: 4, textAlign: 'left' }}>
    <SectionTitle title={title} />
    {isList ? (
      <ListContent items={content.split('\n').filter(item => item.trim())} />
    ) : (
      <TextContent content={content} />
    )}
  </Box>
)

const useTranslatedContent = (translationKey) => {
  const { t } = useTranslation(translationKey)

  const getTranslatedValue = (key) => {
    try {
      return t(key, { defaultValue: undefined }) || null
    } catch {
      return null
    }
  }

  const getTranslatedObject = (key) => {
    try {
      return t(key, { returnObjects: true })
    } catch {
      return null
    }
  }

  const content = getTranslatedObject('content')
  const contentParts = Object.entries(content || {})
    .sort(([keyA], [keyB]) => {
      const numA = parseInt(keyA.replace('part', ''))
      const numB = parseInt(keyB.replace('part', ''))
      return numA - numB
    })

  return {
    title: getTranslatedValue('title'),
    datePublished: getTranslatedValue('datePublished'),
    mainImage: getTranslatedValue('mainImage'),
    mainImageAlt: getTranslatedValue('mainImageAlt'),
    partTitles: getTranslatedObject('partTitles'),
    contentParts
  }
}

const BlogContainer = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Box sx={{ maxWidth: '800px', mx: 'auto', px: { xs: 2, md: 0 }, textAlign: 'left' }}>
      {children}
    </Box>
  </motion.div>
)

function PrevNextNav() {
  const location = useLocation()
  const currentPath = location.pathname
  const ordered = blogs
  const index = ordered.findIndex(b => b.route === currentPath)
  const prev = index > 0 ? ordered[index - 1] : null
  const next = index >= 0 && index < ordered.length - 1 ? ordered[index + 1] : null

  if (!prev && !next) return null

  const brand = '#2E3B55'

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3, alignItems: 'center' }}>
      <Box>
        {prev && (
          <Button
            component={Link}
            to={prev.route}
            startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
            sx={{
              color: brand,
              borderColor: brand,
              textTransform: 'none',
              fontSize: '0.8rem',
              borderRadius: 999,
              px: 1.25,
              py: 0.25,
              minWidth: 0,
              '&:hover': {
                backgroundColor: 'rgba(46,59,85,0.06)',
                borderColor: brand
              }
            }}
            variant="outlined"
            size="small"
          >
            Prev
          </Button>
        )}
      </Box>
      <Box>
        {next && (
          <Button
            component={Link}
            to={next.route}
            endIcon={<ArrowForwardIosIcon sx={{ fontSize: 14 }} />}
            sx={{
              color: brand,
              borderColor: brand,
              textTransform: 'none',
              fontSize: '0.8rem',
              borderRadius: 999,
              px: 1.25,
              py: 0.25,
              minWidth: 0,
              '&:hover': {
                backgroundColor: 'rgba(46,59,85,0.06)',
                borderColor: brand
              }
            }}
            variant="outlined"
            size="small"
          >
            Next
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default function BlogPost({ 
  translationKey,
  seoTitle,
  seoDescription,
  seoKeywords,
  seoTags 
}) {
  const {
    title,
    datePublished,
    mainImage,
    mainImageAlt,
    partTitles,
    contentParts
  } = useTranslatedContent(translationKey)

  return (
    <>
      <PageSEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        tags={seoTags}
      />
      <BlogContainer>
        <StyledTitle>{title}</StyledTitle>
        <StyledDate>{datePublished}</StyledDate>
        <BlogImage image={mainImage} alt={mainImageAlt || title} />
        
        {contentParts.map(([key, value]) => {
          const textValue = typeof value === 'string' ? value : ''
          const isList = textValue.includes('\n') && textValue.split('\n').every(line => line.trim().startsWith('-'))
          return (
            <Section 
              key={key} 
              content={textValue}
              title={partTitles ? partTitles[key] : null}
              isList={isList}
            />
          )
        })}

        <PrevNextNav />
      </BlogContainer>
    </>
  )
}